import {
  bcp47,
  currencyDisplayFor,
  formatCurrency,
} from "@carpybara/i18n/format";
import type { Locale } from "@carpybara/types";
import {
  currencySymbol,
  type CurrencyPreference,
} from "./display-currency";

/**
 * Locale helpers shared by app chrome, server rendering, the vehicle
 * catalogue, detail view and calculators.
 */

export const DEFAULT_LOCALE: Locale = "en";
export const LOCALE_COOKIE = "carpybara-locale";
export const LOCALE_SOURCE_COOKIE = "carpybara-locale-source";
export const MANUAL_LOCALE_SOURCE = "manual";
export const GEO_LOCALE_SOURCE = "geo";

export const SUPPORTED_LOCALES: ReadonlySet<Locale> = new Set<Locale>([
  "en",
  "uk",
  "ru",
  "ka",
  "de",
  "nl",
  "es",
  "lt",
  "ar",
  "pt",
]);

const COUNTRY_LOCALES: Readonly<Record<string, Locale>> = {
  AE: "ar",
  AO: "pt",
  AR: "es",
  AT: "de",
  AU: "en",
  BH: "ar",
  BO: "es",
  BR: "pt",
  CA: "en",
  CH: "de",
  CL: "es",
  CO: "es",
  CR: "es",
  CU: "es",
  DE: "de",
  DO: "es",
  DZ: "ar",
  EC: "es",
  EG: "ar",
  ES: "es",
  GB: "en",
  GE: "ka",
  GT: "es",
  HN: "es",
  IE: "en",
  IQ: "ar",
  JO: "ar",
  KW: "ar",
  LB: "ar",
  LI: "de",
  LT: "lt",
  LY: "ar",
  MA: "ar",
  MX: "es",
  MZ: "pt",
  NG: "en",
  NI: "es",
  NL: "nl",
  NZ: "en",
  OM: "ar",
  PA: "es",
  PE: "es",
  PS: "ar",
  PY: "es",
  PT: "pt",
  QA: "ar",
  RU: "ru",
  SA: "ar",
  SD: "ar",
  SV: "es",
  SY: "ar",
  TN: "ar",
  UA: "uk",
  UY: "es",
  US: "en",
  VE: "es",
  YE: "ar",
};

export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" &&
    SUPPORTED_LOCALES.has(value as Locale)
  );
}

export function localeFromCountry(country: unknown): Locale | null {
  if (typeof country !== "string") return null;
  const locale = COUNTRY_LOCALES[country.trim().toUpperCase()];
  return isSupportedLocale(locale) ? locale : null;
}

export function defaultLocaleForCountry(country: unknown): Locale {
  return localeFromCountry(country) ?? DEFAULT_LOCALE;
}

/**
 * Manual language choice wins over automatic country defaults. The source
 * marker is new, so we also treat a legacy non-English locale cookie as a
 * manual choice; before country defaults existed, that value could only come
 * from the language picker.
 */
export function resolveManualLocalePreference(
  rawLocale: unknown,
  rawSource: unknown,
): Locale | null {
  if (!isSupportedLocale(rawLocale)) return null;
  if (rawSource === MANUAL_LOCALE_SOURCE) return rawLocale;
  if (!rawSource && rawLocale !== DEFAULT_LOCALE) return rawLocale;
  return null;
}

export function resolveGeoLocalePreference(
  rawLocale: unknown,
  rawSource: unknown,
): Locale | null {
  if (!isSupportedLocale(rawLocale)) return null;
  if (rawSource === GEO_LOCALE_SOURCE) return rawLocale;
  return null;
}

// `bcp47` (short locale code → IETF BCP-47 tag), the null-safe
// `formatCurrency` and its `currencyDisplayFor` live in the shared package;
// re-exported so existing `@/lib/locale` imports keep working unchanged.
export { bcp47, currencyDisplayFor, formatCurrency };

/** Convenience: format a number with the user's locale-correct grouping. */
export function formatNum(n: number, locale: string): string {
  return n.toLocaleString(bcp47(locale));
}

// Null-safe display formatters shared by the listing grids. All render "—"
// for missing/invalid values so cards keep their layout without per-call
// fallback handling.

/**
 * Whole-unit currency in the viewer's DISPLAY currency, converting from the
 * value's native one through live FX rates (rates are per-USD).
 *
 * Same maths the /vehicles and /buy-now cards do inline; hoisted here because
 * the East Asia grid and its detail page both need it. When the rate needed
 * for the conversion is missing (FX fetch failed / still loading) it renders
 * the NATIVE amount labelled in its native currency — "$21,460" rather than
 * the un-converted number wearing a "€" sign, which is what an amount-only
 * fallback would produce.
 */
export function formatDisplayMoney(
  value: number | null | undefined,
  {
    locale,
    currency,
    currencyPreference,
    rates,
  }: {
    locale: string;
    currency: string;
    currencyPreference?: string;
    rates?: Record<string, number | undefined>;
  },
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  let target = currencyPreference ?? currency;
  let amount = value;
  if (target !== currency) {
    const nativeRate = rates?.[currency];
    const usdAmount =
      currency === "USD"
        ? value
        : nativeRate && nativeRate > 0
          ? value / nativeRate
          : null;
    const targetRate = target === "USD" ? 1 : rates?.[target];
    if (usdAmount != null && targetRate && targetRate > 0) {
      amount = usdAmount * targetRate;
    } else {
      // No usable rate — show the native amount under its own symbol rather
      // than mislabelling it.
      target = currency;
    }
  }
  return new Intl.NumberFormat(bcp47(locale), {
    style: "currency",
    currency: target,
    currencyDisplay: currencyDisplayFor(target),
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Symbol for the display-currency preference, for inline filter labels.
 *
 * Delegates to the one policy module: this copy silently omitted GBP, so a
 * UK buyer read "$" on the East Asia filter labels.
 */
export function currencySymbolFor(currency: string): string {
  return currencySymbol(currency as CurrencyPreference);
}

/** Whole number with locale grouping, e.g. mileage. */
export function formatWholeNumber(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(bcp47(locale), {
    maximumFractionDigits: 0,
  }).format(value);
}

/** Whole number grouped with spaces, e.g. "285 030". */
export function formatSpacedWholeNumber(
  value: number | string | null | undefined,
): string {
  if (value == null) return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Short date-time, e.g. "Jun 10, 7:30 PM" — auction end stamps and the like. */
export function formatDateTimeShort(
  value: string | null | undefined,
  locale: string,
): string {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp47(locale), {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
